import React, { createContext, forwardRef, useContext } from "react";

import UserAvatar from "../Common/UserAvatar";
import ButtonUI from "./ButtonUI";
import { cornerClass, joinedCorners, mergeRefs, useGridColumns } from "./joined";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

// Într-o listă <ul> / <ol> rândurile devin <li>: ListUI le spune prin context.
const ListUIContext = createContext(false);

/**
 * ListUI — singura listă a aplicației. Înlocuiește lista lipită (ListGroup:
 * notificări, rezervări, rezultatele căutării, setări), rândurile etichetă–valoare
 * (InfoRow), grila de plăcuțe de statistică (StatGrid), grila de bilete, lista de
 * teme, încercările, cartelele de rezervare, lista de lucru din uneltele de examen
 * și grila de elevi. Stilurile: scss/UI/_listUI.scss.
 *
 * title: antetul (titlul de 16px) · count: numărul de lângă titlu, într-o pastilă (poate fi 0)
 * columns: 1 (implicit) | 2 | 3 | … — coloane egale; ultimul rând fără pereche se
 *          întinde pe locul rămas; de la 4 în sus, pe telefon 2 |
 *          "auto" — câte încap, minim 140px (90px pe telefon), rânduri de aceeași înălțime
 * joined: true (implicit) — rânduri lipite la 3px; rândurile de la margine își
 *         rotunjesc singure colțurile de afară la 24 (16 la plăcuțele de statistică),
 *         grupul nu taie nimic | false — rânduri separate la 8px, fiecare cu rază 16
 * glass: rânduri pline (black-p) cu marginea de sticlă (glass-edge), ca în profilul elevului
 * as: "div" (implicit) | "ul" | "ol" — atunci rândurile devin <li>
 *     (cele apăsabile: <li> cu <button> înăuntru)
 * Copiii: ListUI.Item, ListUI.Empty (lista goală), ListUI.More (subsolul).
 */
const ListUI = forwardRef(function ListUI(
   { as: Component = "div", title, count, columns = 1, joined = true, glass = false, className = "", style, children, ...rest },
   ref,
) {
   const auto = columns === "auto";
   const cols = auto ? 0 : Math.max(1, Math.round(Number(columns)) || 1);
   const isList = Component === "ul" || Component === "ol";
   const titled = title != null;

   // coloanele de fapt (pe telefon, la auto-fit) → colțurile de la margine ale fiecărui
   // rând și întinderea ultimului rând fără pereche
   const [measured, gridRef] = useGridColumns(auto ? 1 : cols);
   const kids = React.Children.toArray(children);
   // plăcuțele grupului: rândurile, lista goală și orice element care primește colțurile
   // (CardUI joined: cardurile de categorie, recenzie, videoclip)
   const isTile = (c) => React.isValidElement(c) && (c.type === ListItem || c.type === ListEmpty || Boolean(c.type?.joinedTile));
   const spans = kids.filter(isTile).map((c) => (c.type === ListEmpty || c.props.featured ? "full" : 1));
   // ultimul rând fără pereche se întinde pe locul rămas (2–6 coloane; se numără doar
   // rândurile obișnuite, fără lista goală și plăcuța „featured”)
   if (!auto && measured >= 2 && measured <= 6) {
      const regular = spans.map((s, k) => (s === 1 ? k : -1)).filter((k) => k >= 0);
      const rest = regular.length % measured;
      if (rest) spans[regular[regular.length - 1]] = measured - rest + 1;
   }
   const corners = joined ? joinedCorners(spans, measured) : null;
   let tile = 0;
   const content = kids.map((c) => {
      if (!isTile(c)) return c;
      const k = tile;
      tile += 1;
      return React.cloneElement(c, {
         gridSpan: typeof spans[k] === "number" && spans[k] > 1 ? spans[k] : undefined,
         corners: corners ? corners[k] : undefined,
      });
   });

   const list = (
      <Component
         ref={titled ? gridRef : mergeRefs(ref, gridRef)}
         className={cx(
            "listUI",
            auto ? "listUI--auto" : `listUI--cols${cols}`,
            cols >= 4 && "listUI--mobile2",
            !joined && "listUI--separate",
            glass && "listUI--glass",
            !titled && className,
         )}
         style={{ ...(auto ? null : { "--listUI-cols": cols }), ...(titled ? null : style) }}
         {...(titled ? {} : rest)}
      >
         <ListUIContext.Provider value={isList}>{content}</ListUIContext.Provider>
      </Component>
   );

   if (!titled) return list;

   return (
      <section ref={ref} className={cx("listUI__section", className)} style={style} {...rest}>
         <div className="listUI__head">
            <h4 className="listUI__title">{title}</h4>
            {count != null && <span className="listUI__count">{count}</span>}
         </div>
         {list}
      </section>
   );
});

// Cercul cu inițiale (UserAvatar, ca în StudentItem): inițialele ca text, sau
// { initials, color, person } — cu person, poza lui dacă are; un element se pune așa cum e.
function renderAvatar(avatar) {
   if (React.isValidElement(avatar)) return avatar;
   const a = typeof avatar === "object" ? avatar : { initials: avatar };
   return <UserAvatar person={a.person} color={a.color} initials={a.initials} className="listUIItem__avatar" />;
}

/**
 * ListUI.Item — un rând din listă. Același element e rândul de listă, plăcuța de
 * statistică, rândul etichetă–valoare și plăcuța „Bilet N”; `layout` alege forma.
 *
 * layout: row (implicit: rândul de listă — notificationsPopup__card: black-p, rază 8,
 *         16×20; titlul cu `meta` în dreapta, dedesubt `detail`) |
 *         stat (plăcuța de statistică — wd-fact: eticheta mică deasupra, cifra 16px / 600
 *         tabulară; într-o listă de o coloană, eticheta în stânga și cifra în dreapta) |
 *         info (eticheta în stânga, valoarea 700 în dreapta — confirmDialog__row; black-s,
 *         stă pe fundal black-p) |
 *         tile (plăcuța cu titlu și scor — student-practice__ticket: scorul într-o pastilă;
 *         în grilă „auto” e biletul, într-o coloană e rândul temei)
 * title: titlul (row, tile) · eticheta (stat, info)
 * meta: valoarea titlului — ora, în dreapta, accent 12px (row) · cifra (stat) ·
 *       valoarea (info) · scorul, ca pastilă (tile)
 * detail: textul mic de dedesubt, white-t 13px · aside: în dreapta rândului, centrat
 *         (butonul de ștergere, comutatorul, valoarea) · children: sub detail (bara de
 *         progres, stelele)
 * avatar: cercul cu inițiale în stânga (rândul de elev): "IP" | { initials, color, person }
 * chevron: săgeata din dreapta a rândului de elev (StudentItem; pe telefon se ascunde)
 * tone: ok (verde) | bad (roșu) | progress (albastru deschis) — rezultatul: biletele,
 *       temele, încercările; la stat colorează doar cifra (wd-fact--ok / --bad) |
 *       warn — contur roșu (exam-admin__stat--warn, exam-admin__list-item--warn)
 * color: fundal propriu (culoarea lecției: var(--event-blue)…); textul mic trece pe white-p
 * selected: ales dintr-o listă de lucru — contur accent de 1px (aria-current)
 * active: rândul curent — accent plin · highlight: necitit — 12% accent și punctul din colț
 * compact: 6×12, înălțime 56, textul pe un rând (rezultatele căutării)
 * featured: pe tot rândul grilei; la stat cifra mare, centrată (media din profilul instructorului)
 * onClick: rândul devine <button> (inel de focus înăuntru) · as: alt element ("a", Link, "li")
 * corners / gridSpan: le pune ListUI (colțurile de la margine, întinderea ultimului rând)
 */
const ListItem = forwardRef(function ListItem(
   {
      as,
      layout = "row",
      title,
      meta,
      detail,
      aside,
      avatar,
      chevron = false,
      tone,
      color,
      selected = false,
      active = false,
      highlight = false,
      compact = false,
      featured = false,
      corners,
      gridSpan,
      onClick,
      type,
      className = "",
      style,
      children,
      ...rest
   },
   ref,
) {
   const inList = useContext(ListUIContext);
   const spanStyle = gridSpan ? { gridColumn: `span ${gridSpan}` } : null;
   const Component = as || (onClick ? "button" : inList ? "li" : "div");
   const isButton = Component === "button";
   const interactive = Boolean(onClick) || isButton || Component === "a" || typeof Component !== "string";
   const hasHead = title != null || meta != null;
   const wrapped = inList && Component !== "li";

   const item = (
      <Component
         ref={ref}
         type={isButton ? type || "button" : type}
         onClick={onClick}
         aria-current={selected ? true : undefined}
         className={cx(
            "listUIItem",
            layout !== "row" && `listUIItem--${layout}`,
            interactive && "listUIItem--interactive",
            avatar != null && "listUIItem--avatar",
            compact && "listUIItem--compact",
            featured && "listUIItem--featured",
            color && "listUIItem--colored",
            tone && `listUIItem--${tone}`,
            highlight && "listUIItem--highlight",
            active && "listUIItem--active",
            selected && "listUIItem--selected",
            cornerClass(corners),
            className,
         )}
         style={{ ...(color ? { "--listUIItem-bg": color } : null), ...(wrapped ? null : spanStyle), ...style }}
         {...rest}
      >
         {avatar != null && renderAvatar(avatar)}
         <span className="listUIItem__main">
            {hasHead && (
               <span className="listUIItem__head">
                  {title != null && <span className="listUIItem__title">{title}</span>}
                  {meta != null && <span className="listUIItem__meta">{meta}</span>}
               </span>
            )}
            {detail != null && <span className="listUIItem__detail">{detail}</span>}
            {children}
         </span>
         {aside != null && <span className="listUIItem__aside">{aside}</span>}
         {chevron && renderIcon("arrow", "listUIItem__chevron")}
         {highlight && <span className="listUIItem__dot" aria-hidden="true" />}
      </Component>
   );

   // în <ul> rândul apăsabil stă într-un <li> (o listă are doar <li> drept copii)
   if (wrapped) {
      return (
         <li className="listUIItem__cell" style={spanStyle || undefined}>
            {item}
         </li>
      );
   }
   return item;
});

/**
 * ListUI.Empty — plăcuța de listă goală (createReservationPopup__search-empty):
 * „Nu există potriviri.” în locul rândurilor, cu forma unui rând (black-p, rază 8,
 * text white-t 13px centrat, înălțime minimă 56); pe tot rândul grilei.
 * icon: iconița de deasupra textului (46px, estompată, ca notificationsPopup__empty)
 * as: alt element (în <ul> e <li> singur)
 */
function ListEmpty({ as, icon, corners, gridSpan, className = "", children, ...rest }) {
   const inList = useContext(ListUIContext);
   const Component = as || (inList ? "li" : "div");
   return (
      <Component className={cx("listUIEmpty", icon && "listUIEmpty--icon", cornerClass(corners), className)} {...rest}>
         {renderIcon(icon, "listUIEmpty__icon")}
         <span className="listUIEmpty__text">{children}</span>
      </Component>
   );
}

/**
 * ListUI.More — subsolul listelor lungi (studentsUI__footer, staffChatPopup__listFooter):
 * „Afișate X din Y” și butonul „Afișează încă N” (ButtonUI, pastila neutră). Stă sub
 * listă sau ca ultimul ei copil (atunci ia tot rândul).
 * shown / total: contorul (fără total nu se arată) · next: câte vin la apăsare
 * onMore: apăsarea; butonul apare doar cu onMore și next > 0 (sau cu label)
 * label: alt text pe buton („Încarcă mai multe”) · loading: rotița pe buton
 */
function ListMore({ shown, total, next = 0, onMore, label, loading = false, className = "", children, ...rest }) {
   const inList = useContext(ListUIContext);
   const Component = inList ? "li" : "div";
   const showButton = typeof onMore === "function" && (next > 0 || Boolean(label));
   return (
      <Component className={cx("listUIMore", className)} {...rest}>
         {total != null && (
            <span className="listUIMore__counter" aria-live="polite">
               Afișate {Math.min(shown ?? total, total)} din {total}
            </span>
         )}
         {showButton ? (
            <ButtonUI onClick={onMore} loading={loading}>
               {label || `Afișează încă ${next}`}
            </ButtonUI>
         ) : null}
         {children}
      </Component>
   );
}

ListUI.Item = ListItem;
ListUI.Empty = ListEmpty;
ListUI.More = ListMore;

export default ListUI;
